'use client'
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  FC,
} from 'react'
import { observer } from 'mobx-react-lite'
import { ChromePicker } from 'react-color'
import { ThemeColor } from '../../theme.types'
import { mapCSSVariablesToThemeColor } from '../../themeMapping'
import { ConstructorTheme } from '../../ConstructorTheme.store'

import styles from './ColorInput.module.scss'

interface ColorInputProps {
  store: ConstructorTheme
  colorKey: ThemeColor
}

export const ColorInput: React.FC<ColorInputProps> = observer(
  ({ store, colorKey }) => {
    const [isVisiblePicker, setIsVisiblePicker] = useState(false)
    const pickerRef = useRef<HTMLDivElement>(null)
    const label = mapCSSVariablesToThemeColor[colorKey]
    const { value, visibleValue } = store.getColor(colorKey)

    useEffect(() => {
      if (!isVisiblePicker) return
      const handleClick = (e: MouseEvent) => {
        if (
          pickerRef.current &&
          !pickerRef.current.contains(e.target as Node)
        ) {
          setIsVisiblePicker(false)
        }
      }
      document.addEventListener('mousedown', handleClick)
      return () => document.removeEventListener('mousedown', handleClick)
    }, [isVisiblePicker])

    const changeColor = useCallback(
      (color: string) => {
        store.setColor(colorKey, color)
      },
      [colorKey, store]
    )

    return (
      <div className={styles.container} onScroll={(e) => e.stopPropagation()}>
        <ColorPicker
          value={value}
          visibleValue={visibleValue}
          onChange={changeColor}
        />
        <label className={styles.label}>{label}</label>
      </div>
    )
  }
)

interface ColorPickerProps {
  value: string // Текущее значение цвета
  visibleValue: string // Значение для фона кнопки может быть CSS-переменной
  onChange: (color: string) => void
}
const ColorPicker: FC<ColorPickerProps> = ({
  value,
  visibleValue,
  onChange,
}) => {
  const [isVisiblePicker, setIsVisiblePicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isVisiblePicker) return

    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsVisiblePicker(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isVisiblePicker])

  const togglePicker = useCallback(() => {
    setIsVisiblePicker((prev) => !prev)
  }, [])

  const changeColor = useCallback(
    (color: any) => {
      onChange(color.hex)
    },
    [onChange]
  )

  return (
    <>
      <button
        type="button"
        className={styles.colorButton}
        style={{ background: visibleValue }}
        onClick={togglePicker}
        aria-label="Выбрать цвет"
      />
      {isVisiblePicker && (
        <div className={styles.pickerContainer} ref={pickerRef}>
          <ChromePicker color={value} onChange={changeColor} />
        </div>
      )}
    </>
  )
}
